import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Target, Plus, Trash2, UserPlus, Phone, Mail } from "lucide-react";

const STAGES = [
  { value: "novo", label: "Novo", cls: "bg-slate-500/20 text-slate-500" },
  { value: "contactado", label: "Contactado", cls: "bg-blue-500/20 text-blue-500" },
  { value: "proposta", label: "Proposta", cls: "bg-amber-500/20 text-amber-600" },
  { value: "negociacao", label: "Negociação", cls: "bg-purple-500/20 text-purple-500" },
  { value: "ganho", label: "Ganho", cls: "bg-emerald-500/20 text-emerald-600" },
  { value: "perdido", label: "Perdido", cls: "bg-red-500/20 text-red-500" },
];

const LEAD_STATUSES = [
  { value: "novo", label: "Novo" },
  { value: "contactado", label: "Contactado" },
  { value: "qualificado", label: "Qualificado" },
  { value: "convertido", label: "Convertido" },
  { value: "perdido", label: "Perdido" },
];

export default function CRMPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [opps, setOpps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [leadModal, setLeadModal] = useState(false);
  const [oppModal, setOppModal] = useState(false);
  const [taskModal, setTaskModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", company: "", phone: "", email: "", source: "", notes: "" });
  const [oppForm, setOppForm] = useState({ title: "", customer_name: "", value: "", stage: "novo", notes: "" });
  const [taskForm, setTaskForm] = useState({ title: "", due_date: "", related: "", notes: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [l, o, t] = await Promise.all([
        axios.get(`${API_URL}/crm/leads`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/crm/opportunities`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/crm/tasks`, { headers, withCredentials: true }),
      ]);
      setLeads(l.data); setOpps(o.data); setTasks(t.data);
    } catch { toast.error("Erro ao carregar CRM"); }
  };

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/crm/leads`, leadForm, { headers, withCredentials: true });
      toast.success("Lead criado");
      setLeadModal(false); setLeadForm({ name: "", company: "", phone: "", email: "", source: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateLeadStatus = async (lead, status) => {
    try {
      await axios.put(`${API_URL}/crm/leads/${lead.lead_id}/status?status=${status}`, {}, { headers, withCredentials: true });
      fetchAll();
    } catch { toast.error("Erro ao atualizar"); }
  };

  const convertLead = async (lead) => {
    try {
      await axios.post(`${API_URL}/crm/leads/${lead.lead_id}/convert`, {}, { headers, withCredentials: true });
      toast.success(`${lead.name} convertido em cliente`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao converter"); }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Remover lead "${lead.name}"?`)) return;
    await axios.delete(`${API_URL}/crm/leads/${lead.lead_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const createOpp = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/crm/opportunities`, { ...oppForm, value: toBaseCurrency(oppForm.value) }, { headers, withCredentials: true });
      toast.success("Oportunidade criada");
      setOppModal(false); setOppForm({ title: "", customer_name: "", value: "", stage: "novo", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateStage = async (opp, stage) => {
    try {
      await axios.put(`${API_URL}/crm/opportunities/${opp.opportunity_id}/stage?stage=${stage}`, {}, { headers, withCredentials: true });
      fetchAll();
    } catch { toast.error("Erro ao atualizar fase"); }
  };

  const deleteOpp = async (opp) => {
    if (!window.confirm(`Remover oportunidade "${opp.title}"?`)) return;
    await axios.delete(`${API_URL}/crm/opportunities/${opp.opportunity_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/crm/tasks`, taskForm, { headers, withCredentials: true });
      toast.success("Tarefa criada");
      setTaskModal(false); setTaskForm({ title: "", due_date: "", related: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toggleTask = async (task) => {
    await axios.put(`${API_URL}/crm/tasks/${task.task_id}/toggle`, {}, { headers, withCredentials: true });
    fetchAll();
  };

  const deleteTask = async (task) => {
    await axios.delete(`${API_URL}/crm/tasks/${task.task_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const pipelineByStage = STAGES.map((s) => ({
    ...s,
    count: opps.filter((o) => o.stage === s.value).length,
    total: opps.filter((o) => o.stage === s.value).reduce((sum, o) => sum + (o.value || 0), 0),
  }));

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">CRM</h1>
        <p className="text-muted-foreground">Leads, oportunidades, pipeline e tarefas comerciais</p>
      </div>

      <Tabs defaultValue="leads">
        <TabsList>
          <TabsTrigger value="leads" data-testid="crm-tab-leads">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="opps" data-testid="crm-tab-opps">Oportunidades ({opps.length})</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="crm-tab-tasks">Tarefas ({tasks.filter((t) => !t.done).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setLeadModal(true)} data-testid="add-lead-btn"><Plus className="w-4 h-4 mr-2" /> Novo Lead</Button>
          </div>
          {leads.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Target className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhum lead registado</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {leads.map((l) => (
                <Card key={l.lead_id} data-testid={`lead-card-${l.lead_id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{l.name}</p>
                        {l.company && <p className="text-xs text-muted-foreground">{l.company}</p>}
                      </div>
                      <Select value={l.status} onValueChange={(v) => updateLeadStatus(l, v)}>
                        <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {l.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</p>}
                    {l.email && <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</p>}
                    {l.source && <Badge variant="outline" className="text-xs">Origem: {l.source}</Badge>}
                    <div className="flex gap-2 pt-1">
                      {l.status !== "convertido" && (
                        <Button size="sm" variant="outline" onClick={() => convertLead(l)} data-testid={`convert-lead-${l.lead_id}`}>
                          <UserPlus className="w-4 h-4 mr-1" /> Converter em Cliente
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteLead(l)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="opps" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {pipelineByStage.map((s) => (
              <Card key={s.value}><CardContent className="p-3">
                <Badge className={s.cls}>{s.label}</Badge>
                <p className="text-lg font-bold mt-1">{s.count}</p>
                <p className="text-xs text-muted-foreground font-mono">{formatCurrency(s.total)}</p>
              </CardContent></Card>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setOppModal(true)} data-testid="add-opp-btn"><Plus className="w-4 h-4 mr-2" /> Nova Oportunidade</Button>
          </div>
          <div className="space-y-2">
            {opps.map((o) => (
              <Card key={o.opportunity_id} data-testid={`opp-card-${o.opportunity_id}`}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-semibold">{o.title}</p>
                    <p className="text-xs text-muted-foreground">{o.customer_name} · {formatDate(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-bold">{formatCurrency(o.value)}</p>
                    <Select value={o.stage} onValueChange={(v) => updateStage(o, v)}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid={`opp-stage-${o.opportunity_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteOpp(o)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {opps.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Nenhuma oportunidade</CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setTaskModal(true)} data-testid="add-crm-task-btn"><Plus className="w-4 h-4 mr-2" /> Nova Tarefa</Button>
          </div>
          <div className="space-y-2">
            {tasks.map((t) => (
              <Card key={t.task_id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Checkbox checked={t.done} onCheckedChange={() => toggleTask(t)} data-testid={`task-check-${t.task_id}`} />
                  <div className="flex-1">
                    <p className={`font-medium ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.related}{t.due_date ? ` · até ${t.due_date}` : ""}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteTask(t)}><Trash2 className="w-4 h-4" /></Button>
                </CardContent>
              </Card>
            ))}
            {tasks.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Nenhuma tarefa</CardContent></Card>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={leadModal} onOpenChange={setLeadModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Lead</DialogTitle></DialogHeader>
          <form onSubmit={createLead} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} data-testid="lead-name-input" /></div>
            <div className="space-y-1"><Label>Empresa</Label><Input value={leadForm.company} onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Telefone</Label><Input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} /></div>
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Origem</Label><Input value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })} placeholder="Ex: Referência, Website" /></div>
            <div className="space-y-1"><Label>Notas</Label><Textarea value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLeadModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-lead-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={oppModal} onOpenChange={setOppModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Oportunidade</DialogTitle></DialogHeader>
          <form onSubmit={createOpp} className="space-y-3">
            <div className="space-y-1"><Label>Título *</Label><Input required value={oppForm.title} onChange={(e) => setOppForm({ ...oppForm, title: e.target.value })} data-testid="opp-title-input" /></div>
            <div className="space-y-1"><Label>Cliente</Label><Input value={oppForm.customer_name} onChange={(e) => setOppForm({ ...oppForm, customer_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Valor Estimado</Label><Input type="number" step="0.01" min="0" value={oppForm.value} onChange={(e) => setOppForm({ ...oppForm, value: e.target.value })} data-testid="opp-value-input" /></div>
            <div className="space-y-1">
              <Label>Fase</Label>
              <Select value={oppForm.stage} onValueChange={(v) => setOppForm({ ...oppForm, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOppModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-opp-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={taskModal} onOpenChange={setTaskModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Tarefa</DialogTitle></DialogHeader>
          <form onSubmit={createTask} className="space-y-3">
            <div className="space-y-1"><Label>Título *</Label><Input required value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="crm-task-title-input" /></div>
            <div className="space-y-1"><Label>Data Limite</Label><Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} /></div>
            <div className="space-y-1"><Label>Relacionado com</Label><Input value={taskForm.related} onChange={(e) => setTaskForm({ ...taskForm, related: e.target.value })} placeholder="Cliente / Lead / Oportunidade" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTaskModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-crm-task-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
